import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Checkbox, FormControlLabel, Grid, TextField } from "@mui/material";
import { ModalV2 } from "../../components/Modal/ModalV2";
import { formatNumber } from "../../helpers/getNumberFormats";
import { useForm } from "../../hooks/useForm";
import { useRuleValidator } from "../../hooks/useRuleValidator";
import { PERMISSIONS } from "../../router/permissions";
import { updateWholesalerCreditConditions } from "../../redux/actions/adminUnits/adminUnitsActions";

const { UNITS_WHOLESALER_CONDITIONS_GIRO } = PERMISSIONS;

const FIELD_LIMITS = {
  efe: 20,
};

const WholesalerCreditConditionsForm = ({
  title = "",
  open = false,
  setOpen = () => {},
  data = {},
}) => {
  const dispatch = useDispatch();
  const { isLoadingComp } = useSelector(({ search }) => search);
  const canEditGiro = useRuleValidator(UNITS_WHOLESALER_CONDITIONS_GIRO);

  const initialValues = {
    withholdingPercentage: String(data.creditConditions?.withholdingPercentage || 0),
    withholdingPercentage_: formatNumber({ input: data.creditConditions?.withholdingPercentage }),
    efe: String(data.creditConditions?.efe || 0),
    efe_: formatNumber({ input: data.creditConditions?.efe }),
    showWithholding: data.creditConditions?.showWithholding ?? false,
  };

  const {
    values,
    onChangeNumberV2,
    patchValues,
    errors,
    setFieldErrors,
  } = useForm(initialValues);

  const { withholdingPercentage, withholdingPercentage_, efe, efe_, showWithholding } = values;

  const fieldLimits = useMemo(() => ({
    ...FIELD_LIMITS,
    withholdingPercentage: data.customer?.creditLimit,
  }), [data.customer?.creditLimit]);

  const validateField = (name, value) => {
    const raw = value.replace(/[^\d.]/g, "");
    const num = Number(raw);
    let message = "";

    if (raw !== "" && !isNaN(num)) {
      if (fieldLimits[name] != null && num > fieldLimits[name]) {
        message = `* No puede ser mayor a ${fieldLimits[name]}`;
      }
    }

    setFieldErrors((prev) => ({ ...prev, [name]: message }));
  };

  const handleChange = (e) => {
    onChangeNumberV2(e, { decimals: 2 });
    validateField(e.target.name, e.target.value);
  };

  const handleCheckboxChange = ({ target }) => {
    const updates = { [target.name]: target.checked };
    patchValues(updates);
  };

  const handleFocus = (e) => {
    const { name } = e.target;
    if (Number(values[name]) === 0) {
      patchValues({ [name]: "", [`${name}_`]: "" });
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    const raw = values[name] === "" || values[name] == null ? "0" : values[name];
    patchValues({
      [name]: raw,
      [`${name}_`]: formatNumber({ input: raw }),
    });
  };

  const handleSubmit = async () => {
    const hasErrors = Object.values(errors).some((message) => !!message);
    if (hasErrors) return;

    const form = {
      idUnit: data.id,
      withholdingPercentage: Number(withholdingPercentage),
      showWithholding,
    };
    if (canEditGiro) form.efe = Number(efe);
    const isUpdated = await dispatch(updateWholesalerCreditConditions({ form }));
    if (isUpdated) setOpen();
  };

  return (
    <ModalV2
      title={title}
      open={open}
      onClose={setOpen}
      onSubmit={handleSubmit}
      submitBtnText="Guardar"
      toRight
      hideContentOnLoading={isLoadingComp?.creditLimit ?? false}
      loading={isLoadingComp?.creditLimit}
      isLoading={isLoadingComp?.updateCreditConditions}
    >
      <Grid item xs={12} style={!canEditGiro ? { paddingBottom: 0 } : null }>
        <TextField
          label="Cantidad a retener"
          value={withholdingPercentage_ || withholdingPercentage}
          name="withholdingPercentage"
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          fullWidth
          inputProps={{ style: { textAlign: "right" } }}
          error={!!errors.withholdingPercentage}
          helperText={errors.withholdingPercentage || " "}
        />
      </Grid>
      {canEditGiro && (
        <Grid item xs={12} style={{ paddingBottom: 0 }}>
          <TextField
            label="Giro"
            value={efe_ || efe}
            name="efe"
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            fullWidth
            inputProps={{ style: { textAlign: "right" } }}
            error={!!errors.efe}
            helperText={errors.efe || " "}
          />
        </Grid>
      )}
      <Grid item xs={12} style={{ paddingTop: 0, paddingBottom: 0 }}>
        <FormControlLabel
          label="Ver PDF"
          sx={{
            mx: 0,
            "& .MuiFormControlLabel-label": {
              color: showWithholding ? "success.main" : undefined,
              fontWeight: showWithholding ? 500 : undefined,
            },
          }}
          control={
            <Checkbox
              name="showWithholding"
              checked={showWithholding}
              onChange={handleCheckboxChange}
              color="success"
            />
          }
        />
      </Grid>
    </ModalV2>
  );
};

export default WholesalerCreditConditionsForm;